import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  Box,
  TextField,
  CircularProgress,
  Autocomplete,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Gavel as GavelIcon,
  DirectionsCar as DirectionsCarIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { auctionServices, apiAuth } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { auctionSchema } from "../../utils/Schema";
import BackButton from "../../components/Buttons/BackButton";
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { IMAGE_BASE } from "../../utils/constants";
import { toast } from "react-toastify";
import { capitalizeFirstLetter, trimFormValues } from "../../utils/helpers";
import "../../styles/custom.css";

const AddEditAuction = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [auctionData, setAuctionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();

  const auctionTypes = [
    { value: "normal", label: "Normal Auction", icon: <TrendingUpIcon /> },
    { value: "marketDropAi", label: "MarketDropAI Auction", icon: <TrendingDownIcon /> },
    { value: "claimPoint", label: "ClaimPOINT Auction", icon: <GavelIcon /> },
  ];

  const [initialValues, setInitialValues] = useState({
    vehicleId: "",
    auctionType: "normal",
    description: "",
    startTime: new Date(),
    endTime: new Date(new Date().getTime() + 30 * 60000),
    defaultPriceDropIntervalValue: "",
    defaultPriceDropIntervalUnit: "seconds",
    defaultPriceDropValue: "",
  });

  useEffect(() => {
    if (isEdit && auctionData) {
      setInitialValues({
        vehicleId: auctionData.vehicle?._id || "",
        auctionType: auctionData.auctionType || "normal",
        description: auctionData.description || "",
        startTime: auctionData.schedule?.startTime
          ? new Date(auctionData.schedule.startTime)
          : new Date(),
        endTime: auctionData.schedule?.endTime
          ? new Date(auctionData.schedule.endTime)
          : new Date(new Date().getTime() + 30 * 60000),
        defaultPriceDropIntervalValue: auctionData.defaultPriceDropIntervalValue ?? "",
        defaultPriceDropIntervalUnit: auctionData.defaultPriceDropIntervalUnit || "seconds",
        defaultPriceDropValue: auctionData.priceDropRate ?? "",
      });
    }
  }, [isEdit, auctionData]);
  // Fetch global settings for default price drop values (only on add, not edit)
  const fetchGlobalSettings = async () => {
    try {
      const response = await apiAuth.getSetting();
      const settings = response?.data?.data;
      if (settings) {
        setInitialValues(prev => ({
          ...prev,
          defaultPriceDropIntervalValue: settings.defaultPriceDropIntervalValue ?? "",
          defaultPriceDropIntervalUnit: settings.defaultPriceDropIntervalUnit || "seconds",
          defaultPriceDropValue: settings.defaultPriceDropValue ?? "",
        }));
      }
    } catch (error) {
      // Silently fail - user can still fill manually
      console.error("Failed to fetch global settings:", error);
    }
  };

  useEffect(() => {
    fetchVehicles();
    if (isEdit) {
      fetchAuctionData();
    } else {
      fetchGlobalSettings();
    }
  }, [isEdit, id]);

  const fetchVehicles = async () => {
    try {
      setVehiclesLoading(true);
      const response = await auctionServices.getVehiclesWithoutAuctions();
      setVehicles(response?.data?.data || []);
    } catch (error) {
      errorHandler(error);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const fetchAuctionData = async () => {
    try {
      setLoading(true);
      const response = await auctionServices.getAuctionById({ id });
      const data = response?.data?.data?.auction || response?.data?.data;
      setAuctionData(data);
      if (data?.vehicle) setSelectedVehicle(data.vehicle);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: auctionSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setLoading(true);
        
        const trimmedValues = trimFormValues(values);
        
        const payload = {
          vehicleId: selectedVehicle?._id || trimmedValues.vehicleId,
          auctionType: trimmedValues.auctionType,
          startTime: values.startTime.toISOString(),
          endTime: values.endTime.toISOString(),
          description: trimmedValues.description || "",
          defaultPriceDropValue: Number(trimmedValues.defaultPriceDropIntervalValue) || null,
          defaultPriceDropIntervalUnit: trimmedValues.defaultPriceDropIntervalUnit || undefined,
          priceDropRate: Number(trimmedValues.defaultPriceDropValue) || null,
        };

        if (isEdit) {
          await auctionServices.editAuction({ id, ...payload });
        } else {
          await auctionServices.addAuction(payload);
        }

        navigate("/event-management");
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  if (loading && !auctionData && isEdit) {
    return (
      <Box sx={{ justifyContent: "center", alignItems: "center", height: 400, display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting } =
    formik;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="addedit-user-container">
        <Box className="page-header-section">
          <BackButton
            text={isEdit ? "Edit Event" : "Add New Event"}
            variant="h4"
            link="/event-management"
          />
          <Typography variant="body2" color="text.secondary" className="page-subtitle">
            {isEdit
              ? "Update Event information and settings"
              : "Create a new Event with complete details and configuration"}
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
          <Box className="form-sections-container">
            {/* Vehicle Selection */}
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <DirectionsCarIcon color="primary" />
                    <Typography variant="h5">Vehicle Selection</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Autocomplete
                  options={vehicles}
                  getOptionLabel={(option) =>
                    typeof option === "string"
                      ? option
                      : `${typeof option.make === 'object' ? option.make?.name || '' : option.make || ""} ${option.model || ""}`
                  }
                  value={
                    vehicles.find((v) => v._id === formik.values.vehicleId) || null
                  }
                  disabled={isEdit}
                  onChange={(e, newValue) => {
                    // Check if the selected vehicle already has an active auction
                    if (newValue && newValue.hasActiveAuction) {
                      toast.error("This vehicle is already in an Event", {

                      });
                      // Clear any visual selection immediately
                      formik.setFieldValue("vehicleId", "");
                      setSelectedVehicle(null);
                      // setVehicleInputValue("");
                      return;
                    }

                    formik.setFieldValue("vehicleId", newValue?._id || "");
                    setSelectedVehicle(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  loading={vehiclesLoading}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {option.vehicleImage && option.vehicleImage.length > 0 ? (
                          <img
                            src={IMAGE_BASE + option.vehicleImage[0]}
                            alt={`${typeof option.make === 'object' ? option.make?.name || '' : option.make || ""} ${option.model || ""}`}
                            style={{
                              width: 40,
                              height: 30,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 30,
                              backgroundColor: '#f5f5f5',
                              borderRadius: 4,
                              border: '1px solid #e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              color: '#999'
                            }}
                          >
                            No Image
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {capitalizeFirstLetter(`${typeof option.make === 'object' ? option.make?.name || '' : option.make || ""} ${option.model || ""}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.year} • {option.miles} {option.odometerUnit}
                          </Typography>
                        </Box>
                      </Box>
                      {option.hasActiveAuction && (
                        <CheckCircleIcon color="primary" sx={{ ml: "auto" }} />

                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Vehicle"
                      name="vehicleId"
                      error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)}
                      helperText={formik.touched.vehicleId && formik.errors.vehicleId}
                    />
                  )}
                />

                {selectedVehicle && (
                  <Paper sx={{ mt: 2, p: 2, bgcolor: "grey.50" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle2">Selected Vehicle Details:</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() =>
                          navigate(`/vehicle-management/edit/${selectedVehicle._id}`)
                        }
                      >
                        Edit Vehicle
                      </Button>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item sx={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Make:</strong> {typeof selectedVehicle.make === 'object' ? selectedVehicle.make?.name || '' : selectedVehicle.make || ''}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Interior Color:</strong> {selectedVehicle.interiorColor}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Model:</strong> {selectedVehicle.model}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Year:</strong> {selectedVehicle.year}
                        </Typography>
                      </Grid>
                      <Grid item sx={12} sm={6}>
                        <Typography variant="body2">
                          <strong>VIN:</strong> {selectedVehicle.VIN}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Exterior Color:</strong> {selectedVehicle.exteriorColor}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Odometer:</strong>{" "}
                          {selectedVehicle.miles} {selectedVehicle.odometerUnit}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Original Price:</strong>{" "}
                          {selectedVehicle.originalPrice
                            ? `$${selectedVehicle.originalPrice.toLocaleString()}`
                            : "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </CardContent>
            </Card>

            {/* Auction Type */}
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <GavelIcon color="primary" />
                    <Typography variant="h5">Event Type Selection</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {auctionTypes.map((type) => (
                    <Paper
                      key={type.value}
                      sx={{
                        p: 3,
                        border: formik.values.auctionType === type.value ? 2 : 1,
                        borderColor:
                          formik.values.auctionType === type.value ? "primary.main" : "divider",
                        borderRadius: 2,
                        cursor: "pointer",
                        backgroundColor:
                          formik.values.auctionType === type.value
                            ? "primary.50"
                            : "background.paper",
                      }}
                      onClick={() => formik.setFieldValue("auctionType", type.value)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {type.icon}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {type.label}
                        </Typography>
                        {formik.values.auctionType === type.value && (
                          <CheckCircleIcon color="primary" sx={{ ml: "auto" }} />
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Auction Details */}
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <InfoIcon color="primary" />
                    <Typography variant="h5">Event Details</Typography>
                  </Box>
                }
              />
              <CardContent>
                <TextField
                  name="description"
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </CardContent>
            </Card>

            {/* Scheduling */}
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <ScheduleIcon color="primary" />
                    <Typography variant="h5">Scheduling</Typography>
                  </Box>
                }
              />
              <CardContent>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box className="price-drop-row">
                    <Box className="price-drop-field-wrapper">
                      <MobileDateTimePicker
                        label="Start Date & Time"
                        name="startTime"
                        disabled={Boolean(id)}
                        value={values.startTime ? dayjs(values.startTime) : null}
                        onChange={(event) => {
                          const value = event?.$d;
                          setFieldValue("startTime", value);
                          if (touched.endTime) {
                            setTimeout(() => formik.validateField("endTime"), 0);
                          }
                        }}
                        onClose={() => {
                          formik.setFieldTouched("startTime", true);
                          if (touched.endTime) {
                            formik.validateField("endTime");
                          }
                        }}
                        slotProps={{
                          textField: {
                            error: touched.startTime && Boolean(errors.startTime),
                            helperText: touched.startTime && errors.startTime,
                            fullWidth: true,
                          },
                        }}
                        minDateTime={dayjs()} 
                      />
                    </Box>

                    <Box className="price-drop-field-wrapper">
                      <MobileDateTimePicker
                        label="End Date & Time"
                        name="endTime"
                        disabled={Boolean(id)}
                        value={values.endTime ? dayjs(values.endTime) : null}
                        onChange={(event) => {
                          const value = event?.$d;
                          setFieldValue("endTime", value);
                          setTimeout(() => formik.validateField("endTime"), 0);
                        }}
                        onClose={() => {
                          formik.setFieldTouched("endTime", true);
                          formik.validateField("endTime");
                        }}
                        slotProps={{
                          textField: {
                            error: touched.endTime && Boolean(errors.endTime),
                            helperText: touched.endTime && errors.endTime,
                            fullWidth: true,
                          },
                        }}
                        minDateTime={values.startTime ? dayjs(values.startTime).add(30, 'minute') : dayjs()}
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>
              </CardContent>
            </Card>

            {/* Default Price Drop Settings */}
            {(formik.values.auctionType === "marketDropAi" || formik.values.auctionType === "claimPoint") && (
              <Card elevation={1} className="section-card">
                <CardHeader
                  title={
                    <Box className="section-header">
                      <TrendingDownIcon color="primary" />
                      <Typography variant="h5">Default Price Drop Settings</Typography>
                    </Box>
                  }
                />
                <CardContent>
                <Box className="price-drop-form-container">
                  {/* First Row: 50-50 split */}
                  <Box className="price-drop-row">
                    <Box className="price-drop-field-wrapper">
                      <TextField
                        name="defaultPriceDropIntervalValue"
                        label="Price Drop Interval Value"
                        value={formik.values.defaultPriceDropIntervalValue || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only numbers and decimal point
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            formik.setFieldValue("defaultPriceDropIntervalValue", value);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        fullWidth
                        error={formik.touched.defaultPriceDropIntervalValue && Boolean(formik.errors.defaultPriceDropIntervalValue)}
                        helperText={formik.touched.defaultPriceDropIntervalValue && formik.errors.defaultPriceDropIntervalValue}
                        inputProps={{ 
                          inputMode: "decimal",
                          pattern: "[0-9]*\\.?[0-9]{0,2}",
                          onWheel: (e) => e.target.blur()
                        }}
                      />
                    </Box>
                    <Box className="price-drop-field-wrapper">
                      <FormControl 
                        fullWidth
                        error={formik.touched.defaultPriceDropIntervalUnit && Boolean(formik.errors.defaultPriceDropIntervalUnit)}
                      >
                        <InputLabel id="defaultPriceDropIntervalUnit-label">Price Drop Interval Unit</InputLabel>
                        <Select
                          labelId="defaultPriceDropIntervalUnit-label"
                          id="defaultPriceDropIntervalUnit"
                          name="defaultPriceDropIntervalUnit"
                          label="Price Drop Interval Unit"
                          value={formik.values.defaultPriceDropIntervalUnit || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <MenuItem value="seconds">Seconds</MenuItem>
                          <MenuItem value="minutes">Minutes</MenuItem>
                          <MenuItem value="hours">Hours</MenuItem>
                        </Select>
                        {formik.touched.defaultPriceDropIntervalUnit && formik.errors.defaultPriceDropIntervalUnit && (
                          <FormHelperText>{formik.errors.defaultPriceDropIntervalUnit}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Box>
                  
                  {/* Second Row: Full Width */}
                  <Box className="price-drop-full-width">
                    <TextField
                      name="defaultPriceDropValue"
                      label="Price Drop Value"
                      value={formik.values.defaultPriceDropValue || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow only numbers and decimal point
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          formik.setFieldValue("defaultPriceDropValue", value);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      fullWidth
                      error={formik.touched.defaultPriceDropValue && Boolean(formik.errors.defaultPriceDropValue)}
                      helperText={formik.touched.defaultPriceDropValue && formik.errors.defaultPriceDropValue}
                      inputProps={{ 
                        inputMode: "decimal",
                        pattern: "[0-9]*\\.?[0-9]{0,2}",
                        onWheel: (e) => e.target.blur()
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
            )}

            {/* Submit */}
            <Box className="section-wrapper">
              <Card elevation={1} className="submit-section-transparent">
                <CardContent>
                  <Box className="submit-header">
                    <Typography variant="h6" className="submit-title">
                      Ready to {isEdit ? "Update" : "Create"} Event?
                    </Typography>

                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="submit-description"
                  >
                    {isEdit
                      ? "Review the information above and click update to save your changes."
                      : "Please review all information before creating the Event."}
                  </Typography>
                  <Box className="submit-buttons">
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate("/event-management")}
                      className="cancel-button"
                      disabled={isSubmitting || loading}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmitting || loading}
                      startIcon={isEdit ? <EditIcon /> : <GavelIcon />}
                      className="submit-button"
                    >
                      {isSubmitting || loading
                        ? "Processing..."
                        : `${isEdit ? "Update" : "Create"} Event`}
                    </LoadingButton>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </form>

      </Box>
    </LocalizationProvider>
  );
};

export default AddEditAuction;
